import { makeArticle, verifiedProviderRelated } from "./learnFactory";

export const pageWhereCanIGetSaintTreatment = makeArticle({
  slug: "where-can-i-get-saint-treatment-for-depression",
  title: "Where Can I Get SAINT Treatment for Depression? | Neuro Wellness",
  description:
    "SAINT is offered only at Verified SAINT Providers. Learn how to find one, what to check before you book, and how patients travel to Coral Springs, FL for the 5-day treatment.",
  canonical: "/learn/where-can-i-get-saint-treatment-for-depression",
  h1: "Where Can I Get SAINT Treatment for Depression?",
  breadcrumbLabel: "Where Can I Get SAINT Treatment for Depression?",
  shortAnswer:
    'SAINT® is only available at clinics and hospitals that Magnus Medical, the company that holds the SAINT license, has trained and credentialed as Verified SAINT Providers. A clinic that offers "accelerated TMS" is not automatically offering SAINT. Neuro Wellness TMS Centers of America in Coral Springs, Florida is a Verified SAINT Provider, and most of our SAINT patients travel to us from out of state for the five-day treatment week.',
  keyTakeaways: [
    "SAINT® is only available at Verified SAINT Providers credentialed by Magnus Medical.",
    'A clinic offering "accelerated TMS" is not automatically offering SAINT®.',
    "SAINT® requires fMRI analysis, proprietary targeting software, neuronavigation, and the full 10-sessions-per-day protocol.",
    "Neuro Wellness in Coral Springs, Florida is a Verified SAINT Provider; most SAINT patients travel from out of state.",
    "Self-pay patients typically move from consultation to treatment within a few days.",
  ],
  sections: [
    {
      heading: "Why you can't get SAINT just anywhere",
      paragraphs: [
        "SAINT stands for Stanford Accelerated Intelligent Neuromodulation Therapy. It was developed at Stanford's Brain Stimulation Lab and is now an FDA-cleared system licensed to Magnus Medical. It isn't a setting on a standard TMS machine. It's a complete system that includes functional MRI (fMRI) analysis to find your personal treatment target, Magnus's proprietary targeting software, neuronavigation equipment that guides the coil to that exact spot, and the [[SAINT protocol|/learn/saint-tms-protocol]] itself: 10 sessions a day for five days.",
        "A clinic needs all of these pieces, plus Magnus training, to offer SAINT. That's why the number of [[SAINT locations|/learn/saint-tms-locations]] is still limited, and why many people end up traveling for it.",
      ],
    },
    {
      heading: '"Accelerated TMS" is not the same thing',
      paragraphs: [
        "You may find clinics advertising accelerated TMS, express TMS, or five-day TMS. Some of these are legitimate accelerated protocols, but they are not SAINT unless the clinic is a Verified SAINT Provider. The difference matters because SAINT's published results come from the full system: the fMRI-personalized target, the neuronavigation, and the specific dosing schedule. A generic accelerated protocol without individualized targeting is a different treatment.",
        'The simplest check: ask the clinic directly, "Are you a Verified SAINT Provider?" and confirm they appear on Magnus Medical\'s provider locator.',
      ],
    },
    {
      heading: "How to find a Verified SAINT Provider",
      paragraphs: [
        "Use the Magnus Medical provider locator at magnusmedical.com/find-a-provider. Call the clinic and ask whether they deliver the full SAINT protocol, including the fMRI scan and neuronavigation. Ask how soon they can treat you — wait times vary a great deal between sites, especially hospital-based programs. Ask about cost and coverage up front, since most patients pay out of pocket and some insurance plans in select states cover it.",
      ],
      list: [
        "Use the Magnus Medical provider locator at magnusmedical.com/find-a-provider.",
        "Call the clinic and ask whether they deliver the full SAINT protocol, including the fMRI scan and neuronavigation.",
        "Ask how soon they can treat you. Wait times vary a great deal between sites, especially hospital-based programs.",
        "Ask about [[cost|/learn/saint-tms-cost]] and coverage up front. Most patients pay out of pocket, and some [[insurance|/learn/is-saint-tms-covered-by-insurance]] plans in select states cover it.",
      ],
    },
    {
      heading: "Getting SAINT in South Florida",
      paragraphs: [
        "Neuro Wellness TMS Centers of America is a Verified SAINT Provider at 9734 West Sample Road, Coral Springs, FL 33065. We serve Broward, Miami-Dade, and Palm Beach counties, but most of our SAINT patients come from other states.",
      ],
      list: [
        "Speed. Self-pay patients typically move from consultation to treatment within a few days.",
        "Seven-day availability. Expanded facilities, private treatment suites, and extended care-team hours.",
        "One week on site. Treatment is five consecutive days. Many patients plan a single trip around it, often with a family member or friend.",
        "Two access paths. Self-pay (our most common option), or Blue Cross Blue Shield and the BCBS Federal Employee Program in select states.",
      ],
    },
    {
      heading: "Who SAINT is for",
      paragraphs: [
        "SAINT is FDA-cleared for adults with major depressive disorder whose current episode hasn't improved enough with antidepressant medication. It isn't appropriate for people with non-removable metal in or near the head or an implanted device such as a pacemaker or deep brain stimulator. You'll be screened for these at your consultation.",
      ],
    },
    {
      heading: "What to do next",
      paragraphs: [
        "Finding a provider is the first step. Finding out whether SAINT fits your history is the second, and that's what a consultation is for.",
        "At Neuro Wellness TMS Centers of America, your first appointment is an evaluation, not a commitment. We'll review your medication history and current symptoms and give you a straight answer about whether SAINT is the right next step, and how quickly we can get you in.",
      ],
      links: [
        { to: "/contactus", text: "Schedule a free consultation" },
        { to: "/saint", text: "Learn more about SAINT" },
      ],
    },
  ],
  faqs: [
    {
      question: "Is SAINT available in every state?",
      answer:
        "No. Verified SAINT Providers are located in a limited number of cities, so many patients travel. Check the Magnus Medical provider locator for current sites.",
    },
    {
      question: "Do I need a referral?",
      answer:
        "Not at our clinic. You can call us directly. Records from your psychiatrist or primary care doctor, especially your medication history, are helpful.",
    },
    {
      question: "Can I travel to Florida for SAINT if I live in another state?",
      answer:
        "Yes. Most of our SAINT patients do. Treatment takes five consecutive days, plus a consultation and brain scan beforehand.",
    },
    {
      question: "Is SAINT done in a hospital?",
      answer:
        "It can be, but it doesn't have to be. At Neuro Wellness, SAINT is delivered in a private outpatient setting. No anesthesia and no overnight stay are required.",
    },
  ],
  sources: [
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Magnus Medical. SAINT® Technology and Verified Provider information.",
      url: "https://magnusmedical.com",
    },
    {
      citation:
        "Stanford Medicine. Stanford Brain Stimulation Lab — SAINT/SNT research.",
      url: "https://med.stanford.edu/brainstimulationlab.html",
    },
  ],
  relatedLinks: verifiedProviderRelated,
});
